import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react-analytics-charts/react-analytics-charts/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "custom-dashboards-and-standalone-charts"
    }}>{`Custom Dashboards and Standalone Charts`}</h1>
    <p>{`The `}<a parentName="p" {...{
        "href": "/react-analytics-charts/AnalyticsDashboard"
      }}>{`AnalyticsDashboard`}</a>{` component makes it easy to embed charts onto your site without having to deal with loading and readying the `}<a parentName="p" {...{
        "href": "https://justinmahar.github.io/react-use-analytics-api/"
      }}>{`Google Analytics Embed API`}</a>{`, checking authorization, and adding a view selector. The `}<a parentName="p" {...{
        "href": "/react-analytics-charts/AnalyticsDashboard"
      }}>{`AnalyticsDashboard`}</a>{` will handle all of that overhead for you.`}</p>
    <p><strong parentName="p">{`See `}<a parentName="strong" {...{
          "href": "/react-analytics-charts/AnalyticsDashboard"
        }}>{`AnalyticsDashboard`}</a>{` for the easiest way to embed charts on your site.`}</strong></p>
    <p>{`However, you may want to completely customize how your dashboard looks.`}</p>
    <ul>
      <li parentName="ul">{`You might want more freedom over placement of the ViewSelector, auth buttons, and charts on the page.`}</li>
      <li parentName="ul">{`You might want to handle authorization yourself.`}</li>
      <li parentName="ul">{`You might want to use a `}<a parentName="li" {...{
          "href": "https://reactjs.org/docs/context.html"
        }}>{`React Context`}</a>{` to manage `}<inlineCode parentName="li">{`gapi`}</inlineCode>{` (`}<a parentName="li" {...{
          "href": "https://justinmahar.github.io/react-use-analytics-api/"
        }}>{`Google Analytics Embed API`}</a>{`) state.`}</li>
      <li parentName="ul">{`You might just want to use only the chart components from this library and ignore everything else!`}</li>
    </ul>
    <p>{`Read on to see how you can use the included components, and optionally the `}<a parentName="p" {...{
        "href": "https://justinmahar.github.io/react-use-analytics-api/"
      }}>{`Google Analytics Embed API`}</a>{` hook, to build a completely custom dashboard.`}</p>
    <h2 {...{
      "id": "custom-dashboard-example"
    }}>{`Custom Dashboard Example`}</h2>
    <p>{`To build your own dashboard, you'll need to handle a few things:`}</p>
    <ul>
      <li parentName="ul">{`Load and ready the `}<a parentName="li" {...{
          "href": "https://justinmahar.github.io/react-use-analytics-api/"
        }}>{`Google Analytics Embed API`}</a></li>
      <li parentName="ul">{`Sign the user in and out of Google Analytics`}</li>
      <li parentName="ul">{`Show a view selector (optional—not needed if you know the `}<a parentName="li" {...{
          "href": "/react-analytics-charts/ViewSelector#example"
        }}>{`view ID`}</a>{`)`}</li>
    </ul>
    <p>{`In addition to `}<a parentName="p" {...{
        "href": "/react-analytics-charts/AuthorizeButton"
      }}>{`AuthorizeButton`}</a>{`, `}<a parentName="p" {...{
        "href": "/react-analytics-charts/SignOutButton"
      }}>{`SignOutButton`}</a>{`, and `}<a parentName="p" {...{
        "href": "/react-analytics-charts/ViewSelector"
      }}>{`ViewSelector`}</a>{`, hooks are available for API access via `}<a parentName="p" {...{
        "href": "https://justinmahar.github.io/react-use-analytics-api/"
      }}>{`react-use-analytics-api`}</a>{`, which this library uses internally.`}</p>
    <p>{`Using these, you can build a custom dashboard fairly easily:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { useAnalyticsApi } from "react-use-analytics-api";
import * as React from "react";
import {
  AuthorizeButton,
  SessionsByDateChart,
  SessionsBySourceChart,
  SignOutButton,
  ViewSelector,
} from "react-analytics-charts";

export function CustomDashboardExample(props) {
  const [viewId, setViewId] = React.useState();
  const { ready, gapi, authorized } = useAnalyticsApi();
  return (
    <div>
      {!ready && "Please wait..."}
      {ready && (
        <div>
          {!authorized && (
            <AuthorizeButton gapi={gapi} authOptions={props.authOptions} />
          )}
          {authorized && (
            <div>
              <ViewSelector
                gapi={gapi}
                onChange={(viewId) => setViewId(viewId)}
              />
              <div>
                <SessionsByDateChart gapi={gapi} viewId={viewId} />
                <SessionsBySourceChart gapi={gapi} viewId={viewId} />
              </div>
              <div>
                <SignOutButton gapi={gapi} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
`}</code></pre>
    <h2 {...{
      "id": "standalone-charts"
    }}>{`Standalone Charts`}</h2>
    <h3 {...{
      "id": "using-analytics-dashboard"
    }}>{`Using Analytics Dashboard`}</h3>
    <p>{`If you're already authorized and want to render a chart by itself, you can use an `}<a parentName="p" {...{
        "href": "/react-analytics-charts/AnalyticsDashboard"
      }}>{`AnalyticsDashboard`}</a>{` with a particular `}<a parentName="p" {...{
        "href": "/react-analytics-charts/ViewSelector#example"
      }}>{`view ID`}</a>{` and no `}<a parentName="p" {...{
        "href": "/react-analytics-charts/ViewSelector"
      }}>{`ViewSelector`}</a>{` visible, and no auth buttons. Give each dashboard a unique `}<inlineCode parentName="p">{`dashId`}</inlineCode>{` if there is more than one.`}</p>
    <p>{`This effectively renders charts as standalone.`}</p>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/react-analytics-charts/AnalyticsDashboard#props"
      }}>{`AnalyticsDashboard props`}</a>{` `}<inlineCode parentName="p">{`viewId`}</inlineCode>{`, `}<inlineCode parentName="p">{`hideViewSelector`}</inlineCode>{`, and `}<inlineCode parentName="p">{`hideAuth`}</inlineCode>{` for more info.`}</p>
    <h3 {...{
      "id": "using-google-analytics-embed-api"
    }}>{`Using `}<a parentName="h3" {...{
        "href": "https://justinmahar.github.io/react-use-analytics-api/"
      }}>{`Google Analytics Embed API`}</a></h3>
    <p>{`If you load and ready the API with `}<a parentName="p" {...{
        "href": "https://justinmahar.github.io/react-use-analytics-api/"
      }}>{`react-use-analytics-api`}</a>{`, or already have `}<inlineCode parentName="p">{`gapi`}</inlineCode>{` loaded and readied via your own means, you can add any of the charts by themselves.`}</p>
    <p>{`You will need to be authorized, and need to know the view ID (see the `}<a parentName="p" {...{
        "href": "/react-analytics-charts/ViewSelector#example"
      }}>{`view selector example`}</a>{`). Using the view ID and `}<inlineCode parentName="p">{`gapi`}</inlineCode>{`, you can render any chart on the page by itself.`}</p>
    <p>{`Just `}<a parentName="p" {...{
        "href": "/react-analytics-charts/charts"
      }}>{`choose a chart`}</a>{`, then pass it the ready and authorized `}<a parentName="p" {...{
        "href": "https://justinmahar.github.io/react-use-analytics-api/"
      }}>{`Google Analytics Embed API`}</a>{` (`}<inlineCode parentName="p">{`gapi`}</inlineCode>{`), and the view ID (`}<inlineCode parentName="p">{`viewId`}</inlineCode>{`), and it'll render on the page for you.`}</p>
    <p>{`Be sure to provide each chart with a `}<inlineCode parentName="p">{`container`}</inlineCode>{` ID if you are rendering the same chart type multiple times on the page.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      